import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSeo } from '../../utils/seo';

export default function About() {
  usePageSeo({
    title: 'О проекте Promokod.online',
    description: 'Узнайте больше о Promokod.online: миссия проекта, принципы отбора акций и промокодов, а также то, как мы помогаем экономить на онлайн-покупках.',
    canonicalPath: '/about',
  });

  const advantages = [
    {
      icon: 'ri-coupon-3-line',
      title: 'Тысячи промокодов',
      description: 'Более 5000 актуальных промокодов от популярных магазинов'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Ежедневное обновление',
      description: 'Мы проверяем и обновляем промокоды каждый день'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Проверенные коды',
      description: 'Все промокоды тестируются перед публикацией'
    },
    {
      icon: 'ri-gift-line',
      title: 'Бесплатный сервис',
      description: 'Все промокоды и скидки доступны абсолютно бесплатно'
    }
  ];

  const stats = [
    { number: '500+', label: 'Магазинов' },
    { number: '5000+', label: 'Промокодов' },
    { number: '100K+', label: 'Пользователей' },
    { number: '1M+', label: 'Сэкономлено рублей' }
  ];

  const trustReasons = [
    {
      icon: 'ri-time-line',
      title: 'Работаем с 2020 года',
      description: 'Более 4 лет помогаем пользователям экономить на покупках'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Доверие пользователей',
      description: 'Более 100 000 активных пользователей выбрали наш сервис'
    },
    {
      icon: 'ri-star-line',
      title: 'Только проверенные магазины',
      description: 'Мы сотрудничаем только с надёжными интернет-магазинами'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Поддержка 24/7',
      description: 'Наша команда всегда готова помочь вам с любыми вопросами'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-orange-50 to-blue-50 py-20">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              О проекте Promokod.online
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Мы создали сервис, который помогает миллионам людей экономить на покупках в интернете. 
              Наша миссия — сделать онлайн-шопинг доступнее и выгоднее для каждого.
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Наша миссия
                </h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Мы верим, что каждый заслуживает возможности покупать качественные товары по лучшим ценам. 
                  Promokod.online объединяет тысячи актуальных промокодов и скидок от популярных интернет-магазинов 
                  в одном удобном месте.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Наша команда ежедневно работает над тем, чтобы вы могли находить самые выгодные предложения 
                  и экономить на каждой покупке. Мы проверяем каждый промокод, чтобы вы получали только 
                  работающие коды и актуальные скидки.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-96 bg-gradient-to-br from-orange-100 to-blue-100 rounded-2xl flex items-center justify-center">
                  <div className="w-32 h-32 bg-gradient-to-br from-orange-500 to-blue-600 rounded-full flex items-center justify-center shadow-2xl">
                    <i className="ri-percent-line text-white text-6xl"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">
              Наши преимущества
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {advantages.map((advantage, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-blue-600 rounded-full flex items-center justify-center mb-4">
                    <i className={`${advantage.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {advantage.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gradient-to-br from-orange-500 to-blue-600">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Promokod.online в цифрах
            </h2>
            <div className="grid md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl font-bold text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-xl text-orange-100">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">
              Почему нам доверяют
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {trustReasons.map((reason, index) => (
                <div 
                  key={index}
                  className="flex items-start space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${reason.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Начните экономить уже сегодня
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Присоединяйтесь к тысячам пользователей, которые уже экономят на покупках с Promokod.online
            </p>
            <a 
              href="/"
              className="inline-block bg-gradient-to-r from-orange-500 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-shadow whitespace-nowrap cursor-pointer"
            >
              Найти промокоды
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
